import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { CampaignSearchRequest } from "../../../api/campaign";
import {
  CampaignSearchState,
  useCampaignSearchStore,
  useSetCampaignSearchStore,
} from "../../../stores/campaignSearchStore";

// Keep consistent with Campaign.tsx
export type CampaignStatus = "Running" | "Completed" | "Scheduled";

interface SearchProps {
  request: CampaignSearchRequest;
  setIsSearch: React.Dispatch<React.SetStateAction<boolean>>;
  setRequest: React.Dispatch<React.SetStateAction<CampaignSearchRequest>>;
}

const Search = ({ request, setIsSearch, setRequest }: SearchProps) => {
  const persisted = useCampaignSearchStore();
  const setPersisted = useSetCampaignSearchStore();

  const defaultStatus = "Running";

  const formMethods = useForm<CampaignSearchState>({
    defaultValues: { ...persisted, status: defaultStatus },
  });

  const status = formMethods.watch("status") as CampaignStatus;

  const triggerSearch = useMemo(
    () => () => {
      const values = formMethods.getValues();
      // Write back status to store/request; reset page to 1
      setPersisted(values);
      setRequest((prev) => ({
        ...prev,
        status: values.status as any,
        pagination: { ...prev.pagination, currentPage: 1 },
      }));
      setIsSearch(true);
    },
    [formMethods, setIsSearch, setPersisted, setRequest]
  );

  const handleStatusChange = (
    _: React.MouseEvent<HTMLElement>,
    next: CampaignStatus | null
  ) => {
    if (!next) return;
    formMethods.setValue("status", next, { shouldDirty: true, shouldValidate: false });
    triggerSearch();
  };

  // Trigger once on mount (consistent with SearchBar.tsx behavior)
  useEffect(() => {
    triggerSearch();
  }, []);

  return (
    <FormProvider {...formMethods}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          my: "16px",
          gap: 2,
        }}
      >
        <ToggleButtonGroup
          value={status}
          exclusive
          onChange={handleStatusChange}
          aria-label="Campaign status filter"
          sx={{
            backgroundColor: "white",
            borderRadius: "25px",
            border: "solid 1px #DBDFE1",
            "& .MuiToggleButtonGroup-grouped": {
              border: "none !important",
              borderRadius: "20px !important",
              margin: "0 2px",
              padding: "8px 24px",
              fontWeight: 500,
              textTransform: "none",
              fontSize: "14px",
              "&.Mui-selected": {
                backgroundColor: "#E87722 !important",
                color: "#ffffff !important",
                "&:hover": {
                  backgroundColor: "#E87722 !important",
                },
              },
              "&:not(.Mui-selected)": {
                backgroundColor: "transparent",
                color: "black",
                "&:hover": {
                  backgroundColor: "#e0e0e0",
                },
              },
            },
          }}
        >
          <ToggleButton value="Running">Running</ToggleButton>
          <ToggleButton value="Completed">Completed</ToggleButton>
          <ToggleButton value="Scheduled">Scheduled</ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </FormProvider>
  );
};

export default Search;
